import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import type { User } from "@mc/types"
import { fetchCurrentUser, loginUser, logoutUser, registerUser } from "../api/auth.js"

/**
 * Session state is never derived from a client-readable token (the
 * session cookie is httpOnly by design, see @mc/auth-client). The only
 * reliable way to know "is there an active session" is asking the
 * Gateway, per FRONTEND_REFERENCE.md section 2 and the ISessionStore doc
 * comment. This provider owns that single source of truth for the app.
 */
type AuthStatus = "loading" | "authenticated" | "unauthenticated"

interface AuthContextValue {
  status: AuthStatus
  user: User | null
  login: (username: string, password: string) => Promise<void>
  register: (username: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading")
  const [user, setUser] = useState<User | null>(null)

  useEffect(() => {
    fetchCurrentUser()
      .then(({ user }) => {
        setUser(user)
        setStatus("authenticated")
      })
      .catch(() => {
        setUser(null)
        setStatus("unauthenticated")
      })
  }, [])

  const value: AuthContextValue = {
    status,
    user,
    async login(username, password) {
      const { user } = await loginUser({ username, password })
      setUser(user)
      setStatus("authenticated")
    },
    async register(username, password) {
      const { user } = await registerUser({ username, password })
      setUser(user)
      setStatus("authenticated")
    },
    async logout() {
      await logoutUser().catch(() => {
        // Logout must clear local state even if the request failed (e.g.
        // the session was already gone server-side) — never trap the
        // user behind a broken "authenticated" state.
      })
      setUser(null)
      setStatus("unauthenticated")
    },
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider")
  return ctx
}
